/**
 * The width of the panel down the right of the canvas, and the drag that
 * changes it.
 *
 * The palette and a block's panel are the same column, so the width lives out
 * here and both read it — widening one and finding the other back at its old
 * size would read as a bug.
 *
 * The drag listens on the window rather than the handle, so leaving the handle
 * mid-drag does not drop it, and it is clamped against the measured canvas
 * rather than the viewport: the panel can take 80% of the space it is actually
 * in, whatever the sidebar is doing.
 */

import { useCallback, useRef, useState } from 'react';

export const AOP_PANEL_MIN_WIDTH = 230;
/** The Steps list reads as a list at this width and as a column of words below it. */
export const AOP_STEPS_MIN_WIDTH = 190;
export const AOP_PANEL_MAX_FRACTION = 0.8;

/** How far an arrow key moves the edge, for anyone not using a pointer. */
const KEYBOARD_STEP = 32;

export interface ResizablePanel {
	width: number;
	/** Put on the element the 80% is measured against. */
	containerRef: (node: HTMLDivElement | null) => void;
	onHandlePointerDown: (event: React.PointerEvent) => void;
	onHandleKeyDown: (event: React.KeyboardEvent) => void;
	/**
	 * Open wider than the palette needs, but only while nobody has set a width:
	 * a panel that re-widened itself every time you selected a block would undo
	 * a deliberate narrowing on the next click.
	 */
	widenIfUntouched: (next: number) => void;
}

export interface ResizablePanelOptions {
	/**
	 * Which edge is dragged. A panel down the right grows when its left edge is
	 * pulled left; one down the left grows when its right edge is pulled right.
	 */
	side?: 'left' | 'right';
	minWidth?: number;
}

export const useResizablePanel = (
	initialWidth: number,
	options: ResizablePanelOptions = {}
): ResizablePanel => {
	const side = options.side ? options.side : 'right';
	const minWidth = options.minWidth === undefined ? AOP_PANEL_MIN_WIDTH : options.minWidth;
	const [width, setWidth] = useState(initialWidth);
	const containerNode = useRef<HTMLDivElement | null>(null);

	const containerRef = useCallback((node: HTMLDivElement | null) => {
		containerNode.current = node;
	}, []);

	const maxWidth = useCallback((): number => {
		const node = containerNode.current;
		const available = node ? node.getBoundingClientRect().width : window.innerWidth;
		return Math.max(minWidth, available * AOP_PANEL_MAX_FRACTION);
	}, [minWidth]);

	const clamp = useCallback(
		(next: number): number => Math.min(Math.max(next, minWidth), maxWidth()),
		[maxWidth, minWidth]
	);

	const onHandlePointerDown = useCallback(
		(event: React.PointerEvent) => {
			event.preventDefault();

			const startX = event.clientX;
			const startWidth = width;

			const onMove = (move: PointerEvent) => {
				const delta = move.clientX - startX;
				setWidth(clamp(side === 'right' ? startWidth - delta : startWidth + delta));
			};
			const onUp = () => {
				window.removeEventListener('pointermove', onMove);
				window.removeEventListener('pointerup', onUp);
				window.removeEventListener('pointercancel', onUp);
			};

			window.addEventListener('pointermove', onMove);
			window.addEventListener('pointerup', onUp);
			window.addEventListener('pointercancel', onUp);
		},
		[clamp, side, width]
	);

	const onHandleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			const towards = side === 'right' ? -1 : 1;

			if (event.key === 'ArrowLeft') {
				event.preventDefault();
				setWidth((current) => clamp(current - KEYBOARD_STEP * towards));
			}

			if (event.key === 'ArrowRight') {
				event.preventDefault();
				setWidth((current) => clamp(current + KEYBOARD_STEP * towards));
			}
		},
		[clamp, side]
	);

	const widenIfUntouched = useCallback(
		(next: number) => setWidth((current) => (current === minWidth ? clamp(next) : current)),
		[clamp, minWidth]
	);

	return { width, containerRef, onHandlePointerDown, onHandleKeyDown, widenIfUntouched };
};
